import { useEffect, useId, useState } from 'react';
import type { CustomizationGroup, MenuCategory, MenuItem } from '@cracked-mug/shared';
import { ImageUploadField } from '../../components/admin/ImageUploadField';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { fieldClass } from '../../components/ui/fieldStyles';
import { cn } from '../../lib/cn';
import { CustomizationGroupsEditor } from './CustomizationGroupsEditor';

interface ItemEditorProps {
  open: boolean;
  item: MenuItem | null;
  categories: MenuCategory[];
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    description: string;
    categoryId: string;
    basePriceCents: number;
    imageUrl: string | null;
    isAvailable: boolean;
    isSeasonal: boolean;
    customizationGroups: CustomizationGroup[];
    displayOrder: number;
  }) => Promise<void>;
}

interface FormState {
  name: string;
  description: string;
  categoryId: string;
  basePriceDollars: string;
  imageUrl: string;
  isAvailable: boolean;
  isSeasonal: boolean;
  displayOrder: string;
  customizationGroups: CustomizationGroup[];
}

function initialState(item: MenuItem | null, fallbackCategoryId: string | undefined): FormState {
  if (item) {
    return {
      name: item.name,
      description: item.description,
      categoryId: item.categoryId,
      basePriceDollars: (item.basePriceCents / 100).toFixed(2),
      imageUrl: item.imageUrl ?? '',
      isAvailable: item.isAvailable,
      isSeasonal: item.isSeasonal,
      displayOrder: String(item.displayOrder),
      customizationGroups: item.customizationGroups,
    };
  }
  return {
    name: '',
    description: '',
    categoryId: fallbackCategoryId ?? '',
    basePriceDollars: '0.00',
    imageUrl: '',
    isAvailable: true,
    isSeasonal: false,
    displayOrder: '0',
    customizationGroups: [],
  };
}

export function ItemEditor({ open, item, categories, onClose, onSubmit }: ItemEditorProps) {
  const [form, setForm] = useState<FormState>(() => initialState(item, categories[0]?.categoryId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initialState(item, categories[0]?.categoryId));
      setError(null);
    }
  }, [open, item, categories]);

  const handleSubmit = async (): Promise<void> => {
    if (form.name.trim().length === 0) {
      setError('Name is required');
      return;
    }
    if (form.categoryId.length === 0) {
      setError('Please pick a category');
      return;
    }
    const basePriceCents = Math.round(Number.parseFloat(form.basePriceDollars) * 100);
    if (!Number.isFinite(basePriceCents) || basePriceCents < 0) {
      setError('Base price must be a non-negative number');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        description: form.description.trim(),
        categoryId: form.categoryId,
        basePriceCents,
        imageUrl: form.imageUrl.trim().length > 0 ? form.imageUrl.trim() : null,
        isAvailable: form.isAvailable,
        isSeasonal: form.isSeasonal,
        customizationGroups: form.customizationGroups,
        displayOrder: Number.parseInt(form.displayOrder, 10) || 0,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={item ? `Edit ${item.name}` : 'New item'}
      className="max-w-2xl"
    >
      <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
        <h2 className="font-display text-2xl">{item ? `Edit ${item.name}` : 'New item'}</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name" className="sm:col-span-2">
            {(id) => (
              <input
                id={id}
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={80}
                className={fieldClass}
              />
            )}
          </Field>

          <Field label="Description" className="sm:col-span-2">
            {(id) => (
              <textarea
                id={id}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                maxLength={280}
                rows={2}
                className={cn(fieldClass, 'resize-none')}
              />
            )}
          </Field>

          <Field label="Category">
            {(id) => (
              <select
                id={id}
                value={form.categoryId}
                onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
                className={fieldClass}
              >
                {categories.map((c) => (
                  <option key={c.categoryId} value={c.categoryId}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Base price (USD)">
            {(id) => (
              <div className="flex items-center gap-1">
                <span className="text-sm text-wca-gray-mid">$</span>
                <input
                  id={id}
                  type="number"
                  min={0}
                  step={0.25}
                  value={form.basePriceDollars}
                  onChange={(e) => setForm((f) => ({ ...f, basePriceDollars: e.target.value }))}
                  className={cn(fieldClass, 'flex-1 font-mono')}
                />
              </div>
            )}
          </Field>

          <Field label="Photo (optional)" className="sm:col-span-2" labelAsHeading>
            {() => (
              <ImageUploadField
                value={form.imageUrl}
                onChange={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
              />
            )}
          </Field>

          <Field label="Display order" hint="Within category. Lower first.">
            {(id) => (
              <input
                id={id}
                type="number"
                min={0}
                value={form.displayOrder}
                onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))}
                className={cn(fieldClass, 'w-24 font-mono')}
              />
            )}
          </Field>

          <div className="flex items-center gap-5">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isAvailable}
                onChange={(e) => setForm((f) => ({ ...f, isAvailable: e.target.checked }))}
                className="w-5 h-5 accent-wca-red"
              />
              Available
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isSeasonal}
                onChange={(e) => setForm((f) => ({ ...f, isSeasonal: e.target.checked }))}
                className="w-5 h-5 accent-wca-red"
              />
              Seasonal
            </label>
          </div>
        </div>

        <section>
          <h3 className="font-display text-xl mb-2">Customizations</h3>
          <CustomizationGroupsEditor
            groups={form.customizationGroups}
            onChange={(groups) => setForm((f) => ({ ...f, customizationGroups: groups }))}
          />
        </section>

        {error !== null && (
          <p className="text-sm text-wca-red bg-wca-red-light rounded-lg p-3">{error}</p>
        )}
      </div>

      <div className="px-6 pb-6 pt-3 border-t border-wca-gray-light flex justify-end gap-3">
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}

function Field({
  label,
  hint,
  className,
  labelAsHeading = false,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  labelAsHeading?: boolean;
  children: (id: string) => React.ReactNode;
}) {
  const id = useId();
  return (
    <div className={`space-y-1.5${className ? ` ${className}` : ''}`}>
      {labelAsHeading ? (
        <span className="block text-sm font-medium">{label}</span>
      ) : (
        <label htmlFor={id} className="block text-sm font-medium">
          {label}
        </label>
      )}
      {children(id)}
      {hint !== undefined && <p className="text-xs text-wca-gray-mid">{hint}</p>}
    </div>
  );
}
